import { LogOut, ShieldAlert, X, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AgenteReprovadoScreenProps {
  nomeAgente?: string;
  nomeOuvidoria?: string;
  motivo?: string;
  onLogout?: () => void;
}

/**
 * Tela exibida quando o agente logado possui status `Reprovado`.
 * Bloqueia totalmente o acesso ao sistema e apresenta explicação + opção de sair.
 */
export function AgenteReprovadoScreen({
  nomeAgente,
  nomeOuvidoria,
  motivo,
  onLogout,
}: AgenteReprovadoScreenProps) {
  const handleClose = () => {
    // Tenta fechar a aba; se o navegador bloquear, faz logout como fallback.
    window.close();
    setTimeout(() => {
      onLogout?.();
    }, 150);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background overflow-y-auto">
      {/* Gradiente decorativo de fundo */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 10%, hsl(var(--destructive) / 0.18), transparent 50%), radial-gradient(circle at 80% 90%, hsl(var(--primary) / 0.12), transparent 55%)",
        }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-2xl mx-4 my-8 animate-fade-in">
        <div className="surface-elevated rounded-2xl border border-destructive/20 shadow-2xl p-6 sm:p-10 space-y-6 text-center">
          {/* Badge de status */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-destructive/10 text-destructive text-xs font-semibold border border-destructive/20 mx-auto">
            <ShieldAlert className="h-3.5 w-3.5" />
            Acesso negado
          </div>

          {/* Ilustração */}
          <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto flex items-center justify-center relative">
            <div className="absolute inset-0 rounded-full bg-destructive/10 animate-pulse" />
            <ShieldAlert className="relative w-20 h-20 sm:w-24 sm:h-24 text-destructive animate-bounce" style={{ animationDuration: '2s' }} />
          </div>

          {/* Título */}
          <div className="space-y-2">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
              {nomeAgente ? `${nomeAgente}, ` : ""}seu cadastro foi reprovado
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-lg mx-auto">
              Infelizmente sua conta não foi aprovada
              {nomeOuvidoria ? (
                <>
                  {" "}para atuar na ouvidoria{" "}
                  <span className="font-semibold text-foreground">
                    {nomeOuvidoria}
                  </span>
                </>
              ) : null}
              . Por isso, o acesso ao sistema está indisponível no momento.
            </p>
          </div>

          {/* Justificativa (se existir) */}
          {motivo && (
            <div className="text-left bg-muted/40 border border-border rounded-xl p-4 sm:p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Justificativa da ouvidoria
              </p>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                {motivo}
              </p>
            </div>
          )}

          {/* Ajuda / contato */}
          <div className="flex items-start gap-3 text-left bg-primary/5 border border-primary/15 rounded-xl p-4 sm:p-5">
            <Mail className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground">
                Discorda desta decisão?
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Entre em contato diretamente com o gestor da ouvidoria
                {nomeOuvidoria ? (
                  <>
                    {" "}
                    <span className="font-semibold text-foreground">
                      {nomeOuvidoria}
                    </span>
                  </>
                ) : null}{" "}
                para solicitar uma revisão do seu cadastro ou obter mais
                informações.
              </p>
            </div>
          </div>

          {/* Ações */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button
              variant="outline"
              size="lg"
              onClick={onLogout}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              Sair da conta
            </Button>
            <Button
              variant="destructive"
              size="lg"
              onClick={handleClose}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Fechar o site
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgenteReprovadoScreen;
